// src/pages/Accounting/JournalEntries/JournalEntryPrint.jsx
import React, { forwardRef } from 'react';
import dayjs from 'dayjs';
import ReportPrintHeader from '../../../components/ReportPrintHeader';
import { tafqeet } from '../../../utils/tafqeet';
import { entryTypeLabel } from './journalEntryTypes';

const money = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const C = { ink: '#111827', muted: '#6b7280', line: '#d1d5db', soft: '#f3f4f6' };
const td = { border: `1px solid ${C.line}`, padding: '6px 8px', fontSize: 12.5, verticalAlign: 'top' };
const th = { ...td, background: C.soft, fontWeight: 800, textAlign: 'right', whiteSpace: 'nowrap' };
const numCell = { ...td, direction: 'ltr', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

/**
 * data: { entryNumber, date, entryType, typeLabel?, statusLabel?, referenceNumber, description, lines:[{accountName, description, debit, credit}] }
 * entryTypes (اختياري — للتوافق مع النسخة القديمة)
 */
const JournalEntryPrint = forwardRef(({ data, entryTypes, companyInfo }, ref) => {
  if (!data) return null;

  const typeLabel = data.typeLabel
    || entryTypes?.find(t => t.value === data.entryType)?.label
    || entryTypeLabel(data.entryType);
  const lines = (data.lines || []).filter(l => l.accountId || Number(l.debit) || Number(l.credit));
  const totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);

  return (
    <div ref={ref} dir="rtl" className="je-print" style={{
      direction: 'rtl', background: '#fff', color: C.ink, padding: '8mm 10mm',
      fontFamily: 'Tajawal, Cairo, "Segoe UI", Tahoma, sans-serif', boxSizing: 'border-box',
    }}>
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .je-print thead { display: table-header-group; }
          .je-print tr { page-break-inside: avoid; }
        }
        .je-print table { border-collapse: collapse; width: 100%; }
      `}</style>

      <ReportPrintHeader title={typeLabel} subTitle={`رقم القيد: ${data.entryNumber || '—'}`} company={companyInfo} />

      {/* شريط البيانات */}
      <div style={{ display: 'flex', border: `2px solid ${C.ink}`, borderRadius: 8, overflow: 'hidden', margin: '10px 0 12px' }}>
        {[
          ['نوع القيد', typeLabel],
          ['رقم القيد', data.entryNumber || '—'],
          ['التاريخ', data.date ? dayjs(data.date).format('DD/MM/YYYY') : '—'],
          ['المرجع', data.referenceNumber || '—'],
          ...(data.statusLabel ? [['الحالة', data.statusLabel]] : []),
        ].map(([k, v], i) => (
          <div key={k} style={{ flex: 1, padding: '6px 10px', textAlign: 'center', borderRight: i ? `1px solid ${C.ink}` : 'none' }}>
            <div style={{ fontSize: 11, color: C.muted }}>{k}</div>
            <div style={{ fontSize: 14, fontWeight: 800 }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 12, fontSize: 13.5 }}>
        <span style={{ fontWeight: 800 }}>البيان: </span>{data.description || '—'}
      </div>

      {/* السطور */}
      <table style={{ marginBottom: 12 }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 30, textAlign: 'center' }}>#</th>
            <th style={{ ...th, width: '34%' }}>الحساب</th>
            <th style={th}>البيان</th>
            <th style={{ ...th, width: 105 }}>مدين</th>
            <th style={{ ...th, width: 105 }}>دائن</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={l.id ?? i}>
              <td style={{ ...td, textAlign: 'center', color: C.muted }}>{i + 1}</td>
              <td style={{ ...td, fontWeight: 600 }}>{l.accountName || l.accountId || '—'}</td>
              <td style={td}>{l.description || ''}</td>
              <td style={numCell}>{Number(l.debit) ? money(l.debit) : ''}</td>
              <td style={numCell}>{Number(l.credit) ? money(l.credit) : ''}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} style={{ ...th, borderTop: `2px solid ${C.ink}` }}>الإجمالي</td>
            <td style={{ ...numCell, fontWeight: 800, background: C.soft, borderTop: `2px solid ${C.ink}` }}>{money(totalDebit)}</td>
            <td style={{ ...numCell, fontWeight: 800, background: C.soft, borderTop: `2px solid ${C.ink}` }}>{money(totalCredit)}</td>
          </tr>
        </tfoot>
      </table>

      {totalDebit > 0 && (
        <div style={{ border: `1px dashed ${C.line}`, borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
          <span style={{ fontWeight: 800 }}>مبلغ القيد: </span>{tafqeet(totalDebit)}
        </div>
      )}

      {/* التوقيعات */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginTop: 36 }}>
        {['أعدّه', 'راجعه', 'اعتمده'].map(s => (
          <div key={s} style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>{s}</div>
            <div style={{ marginTop: 38, borderBottom: `1px solid ${C.ink}`, width: '80%', marginInline: 'auto' }} />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>الاسم والتوقيع</div>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 22, paddingTop: 6, borderTop: `1px solid ${C.line}`,
        display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.muted,
      }}>
        <span>طُبع من نظام أرباح | Profit ERP</span>
        <span style={{ direction: 'ltr' }}>{dayjs().format('DD/MM/YYYY HH:mm')}</span>
      </div>
    </div>
  );
});

JournalEntryPrint.displayName = 'JournalEntryPrint';
export default JournalEntryPrint;
